import { useEffect, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { operatorConsole, type PackBucketRow, type PackBuckets, type PackBucketsCheck } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { BucketsTable } from './BucketsTable';

type Props = {
  poolOfferId: number;
  name: string;
  /** The listed price, until the saved ladder brings the pack's own. */
  price: number;
  /** The operator prices this pack with fixed rates, which Min % does not move. */
  fixedRates: boolean;
  onSaved?: (buckets: PackBuckets) => void;
  /** Failures that are not about the ladder itself go to the page's error line. */
  onError: (message: string) => void;
};

/** A dry run and the draft it answered: Save trusts only an answer for the draft on screen. */
type Check = { groups: PackBucketRow[]; result: PackBucketsCheck };

type Notice = { tone: 'success' | 'error'; text: string };

type LockedReason = NonNullable<PackBuckets['locked_reason']>;

export const BUCKETS_VALIDATE_DEBOUNCE_MS = 400;
export const BUCKETS_RETRY_AFTER_MS = 5000;
const MIN_SUGGEST_PRICE = 3;
const NICE_STEPS: [limit: number, step: number][] = [
  [10, 1],
  [50, 5],
  [100, 10],
  [500, 25],
  [1000, 50],
];
const NICE_DEFAULT_STEP = 100;

const SHARED_NOTE = 'Shared pack: these buckets apply everywhere this pack is sold.';
const FIXED_RATES_NOTE = "Min % does not change your fixed-rate odds; it shapes the pack's own odds and other sellers'.";
const LOCKED_REASONS: Record<LockedReason, string> = {
  card_rules: 'This pack has card rules managed by support',
  static_chances: "This pack's odds are fixed by support, buckets do not apply",
};
const SAVE_CONFIRM = 'Odds are re-solved for this pack right away, for everyone who sells it. Continue?';
const SAVED_LINE = 'Buckets saved. Odds and tiers are updated.';
const THROTTLED_LINE = 'Too many checks in a row — the preview refreshes in a few seconds.';
const CHANGED_BY: Record<NonNullable<PackBuckets['last_change']>['by'], string> = {
  you: 'you',
  another_seller: 'another seller',
  support: 'Repackz support',
};

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function sameGroups(a: PackBucketRow[], b: PackBucketRow[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function answers(check: Check | null, draft: PackBucketRow[]): check is Check {
  return check != null && (check.groups === draft || sameGroups(check.groups, draft));
}

function isLockedReason(value: unknown): value is LockedReason {
  return value === 'card_rules' || value === 'static_chances';
}

/** GroupsTemplate.nice: a whole-dollar edge whose step grows with the value. */
function niceEdge(value: number): number {
  if (value <= 1) return 1;
  const step = NICE_STEPS.find(([limit]) => value < limit)?.[1] ?? NICE_DEFAULT_STEP;
  return Math.max(Math.round(value / step) * step, 1);
}

/** GroupsTemplate's ladder around the whole-number price under a bucket from $0, so no card falls below the first bucket. */
function suggestedLadder(price: number): PackBucketRow[] {
  const edge = Math.floor(price);
  const edges = [
    ...new Set([0, niceEdge(edge / 10), niceEdge(edge / 2), edge, niceEdge(edge * 2), niceEdge(edge * 5)]),
  ];
  return edges.map((from, i) => ({ from, to: edges[i + 1] ?? null, floor: null }));
}

function withRowAdded(groups: PackBucketRow[]): PackBucketRow[] {
  const last = groups[groups.length - 1];
  if (!last) return [{ from: 0, to: null, floor: null }];
  const from = last.to ?? last.from * 2;
  return [...groups.slice(0, -1), { ...last, to: from }, { from, to: null, floor: null }];
}

/** The neighbour takes over the removed bucket's range, so the ladder stays joined. */
function withRowRemoved(groups: PackBucketRow[], index: number): PackBucketRow[] {
  const removed = groups[index];
  if (!removed) return groups;
  const next = groups.filter((_, i) => i !== index);
  const previous = next[index - 1];
  const following = next[index];
  if (previous) next[index - 1] = { ...previous, to: removed.to };
  else if (following) next[index] = { ...following, from: removed.from };
  return next;
}

/** The backend may send `error_code` in capitals next to a lowercase `error`. */
function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? (err.code?.toLowerCase() ?? null) : null;
}

function refusedLadder(err: unknown): PackBucketsCheck | null {
  if (!(err instanceof ApiError) || errorCode(err) !== 'buckets_invalid') return null;
  return (err.details?.validation as PackBucketsCheck | undefined) ?? null;
}

function lockedReasonOf(err: unknown): LockedReason | null {
  if (!(err instanceof ApiError) || errorCode(err) !== 'not_editable') return null;
  const reason = err.details?.locked_reason;
  return isLockedReason(reason) ? reason : null;
}

function lastChangeLine(change: NonNullable<PackBuckets['last_change']>): string {
  const day = new Date(change.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `Last change: by ${CHANGED_BY[change.by]}, ${day}`;
}

/** The pack's shared ladder: a draft checked by the backend's dry run as it changes, written only on Save. */
export function PackBucketsEditor({ poolOfferId, name, price: listedPrice, fixedRates, onSaved, onError }: Props) {
  const [saved, setSaved] = useState<PackBuckets | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState<PackBucketRow[]>([]);
  const [check, setCheck] = useState<Check | null>(null);
  const [checking, setChecking] = useState(false);
  const [throttled, setThrottled] = useState(false);
  const [retries, setRetries] = useState(0);
  const [yourShareShown, setYourShareShown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [locked, setLocked] = useState<LockedReason | null>(null);
  const editable = (saved?.editable ?? false) && !locked;
  const price = saved?.price ?? listedPrice;
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    operatorConsole.packBuckets
      .get(poolOfferId)
      .then((buckets) => {
        if (cancelled) return;
        setSaved(buckets);
        setDraft(buckets.groups);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadFailed(true);
        onErrorRef.current(`Could not load the buckets: ${errorText(err)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [poolOfferId]);

  useEffect(() => {
    if (!editable || answers(check, draft)) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    setChecking(true);
    const timer = setTimeout(() => {
      operatorConsole.packBuckets
        .validate(poolOfferId, draft)
        .then((result) => {
          if (cancelled) return;
          setCheck({ groups: draft, result });
          if (result.buckets.some((b) => b.your_share != null)) setYourShareShown(true);
          setThrottled(false);
          setChecking(false);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setChecking(false);
          if (err instanceof ApiError && err.status === 429) {
            setThrottled(true);
            retry = setTimeout(() => setRetries((n) => n + 1), BUCKETS_RETRY_AFTER_MS);
            return;
          }
          setThrottled(false);
          onErrorRef.current(`Could not check the buckets: ${errorText(err)}`);
        });
    }, BUCKETS_VALIDATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(retry);
    };
  }, [poolOfferId, editable, draft, check, retries]);

  if (!saved) {
    return (
      <div role="group" aria-label={`Buckets for ${name}`} className="text-[12px] text-[var(--co-muted)]">
        {loadFailed ? (
          'Could not load the buckets.'
        ) : (
          <span className="inline-flex items-center gap-2">
            <LoaderCircle size={14} aria-hidden="true" className="animate-spin" />
            Loading buckets…
          </span>
        )}
      </div>
    );
  }

  const dirty = !sameGroups(draft, saved.groups);
  const result = answers(check, draft) ? check.result : null;
  const checked = result?.ok ?? false;

  function change(next: PackBucketRow[]) {
    setDraft(next);
    setNotice(null);
  }

  function discard() {
    change(saved!.groups);
  }

  async function save() {
    if (!window.confirm(SAVE_CONFIRM)) return;
    setSaving(true);
    setNotice(null);
    try {
      const { buckets, validation } = await operatorConsole.packBuckets.update(poolOfferId, draft);
      setSaved(buckets);
      setDraft(buckets.groups);
      setCheck({ groups: buckets.groups, result: validation });
      setNotice({ tone: 'success', text: SAVED_LINE });
      onSaved?.(buckets);
    } catch (err) {
      const refused = refusedLadder(err);
      const lockedReason = lockedReasonOf(err);
      if (refused) setCheck({ groups: draft, result: refused });
      else if (lockedReason) {
        setLocked(lockedReason);
        setDraft(saved!.groups);
      } else if (errorCode(err) === 'pack_busy') setNotice({ tone: 'error', text: errorText(err) });
      else onError(`Could not save the buckets: ${errorText(err)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div role="group" aria-label={`Buckets for ${name}`} className="flex max-w-[1100px] flex-col gap-3">
      {(saved.shared || fixedRates || (!editable && saved.locked_reason)) && (
        <div className="flex flex-col gap-1 text-[11px]">
          {saved.shared && <p className="text-[var(--co-warning)]">{SHARED_NOTE}</p>}
          {fixedRates && <p className="text-[var(--co-muted)]">{FIXED_RATES_NOTE}</p>}
          {!editable && saved.locked_reason && (
            <p className="font-medium text-[var(--co-text)]">{LOCKED_REASONS[saved.locked_reason]}</p>
          )}
        </div>
      )}

      <BucketsTable
        groups={draft}
        price={price}
        disabled={!editable || saving}
        onChange={change}
        {...(editable
          ? {
              onAddRow: () => change(withRowAdded(draft)),
              onRemoveRow: (index: number) => change(withRowRemoved(draft, index)),
              preview: { buckets: result?.buckets ?? null, yourShare: yourShareShown },
              ...(price >= MIN_SUGGEST_PRICE ? { onSuggest: () => change(suggestedLadder(price)) } : {}),
            }
          : {})}
      />

      {locked && (
        <p role="alert" className="text-[11px] font-medium text-[var(--co-error)]">
          {LOCKED_REASONS[locked]}
        </p>
      )}

      {!editable && saved.last_change && (
        <p className="text-[11px] text-[var(--co-muted)]">{lastChangeLine(saved.last_change)}</p>
      )}

      {editable && (
        <>
          <div aria-live="polite" className="flex flex-col gap-1 text-[11px]">
            {result?.errors.map((issue, i) => (
              <p key={`e${i}`} className="text-[var(--co-error)]">
                <span aria-hidden="true">✕ </span>
                <span>{issue.message}</span>
              </p>
            ))}
            {result?.warnings.map((issue, i) => (
              <p key={`w${i}`} className="text-[var(--co-warning)]">
                <span aria-hidden="true">! </span>
                <span>{issue.message}</span>
              </p>
            ))}
            {result?.ok && result.warnings.length === 0 && (
              <p className="text-[var(--co-success)]">Ladder is valid</p>
            )}
            {!result && checking && <p className="text-[var(--co-muted)]">Checking…</p>}
            {!result && !checking && throttled && <p className="text-[var(--co-muted)]">{THROTTLED_LINE}</p>}
            {notice && (
              <p
                role={notice.tone === 'error' ? 'alert' : 'status'}
                className={`font-medium ${notice.tone === 'error' ? 'text-[var(--co-error)]' : 'text-[var(--co-success)]'}`}
              >
                {notice.text}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            {saved.last_change && (
              <span className="mr-auto text-[11px] text-[var(--co-muted)]">{lastChangeLine(saved.last_change)}</span>
            )}
            {dirty && <span className="text-[11px] text-[var(--co-muted)]">Unsaved changes</span>}
            <Button variant="ghost" onClick={discard} disabled={!dirty || saving}>
              Discard
            </Button>
            <Button onClick={() => void save()} disabled={!dirty || !checked || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
